export interface CameraShakeOptions {
  decayRate?: number;
  maxTranslation?: number;
  maxRotationDeg?: number;
  frequency?: number;
}

export interface ShakeOffset {
  x: number;
  y: number;
  z: number;
  pitch: number;
  yaw: number;
  roll: number;
}

export class CameraShake {
  private trauma: number = 0;
  private time: number = 0;
  public readonly decayRate: number;
  public readonly maxTranslation: number;
  public readonly maxRotationRad: number;
  public readonly frequency: number;

  constructor(options: CameraShakeOptions = {}) {
    this.decayRate = options.decayRate ?? 1.5;
    this.maxTranslation = options.maxTranslation ?? 0.6;
    this.maxRotationRad = ((options.maxRotationDeg ?? 3.0) * Math.PI) / 180;
    this.frequency = options.frequency ?? 25.0;
  }

  public getTrauma(): number {
    return this.trauma;
  }

  public addTrauma(amount: number): void {
    this.trauma = Math.min(1.0, this.trauma + amount);
  }

  public update(dt: number): void {
    if (this.trauma > 0) {
      this.time += dt * this.frequency;
      this.trauma = Math.max(0, this.trauma - this.decayRate * dt);
    }
  }

  public getOffset(): ShakeOffset {
    if (this.trauma <= 0) {
      return { x: 0, y: 0, z: 0, pitch: 0, yaw: 0, roll: 0 };
    }

    const shake = this.trauma * this.trauma; // quadratic falloff
    // Multi-frequency harmonic sinusoids approximating Perlin noise
    const nx = Math.sin(this.time * 1.0) * 0.7 + Math.sin(this.time * 2.3) * 0.3;
    const ny = Math.cos(this.time * 1.3) * 0.7 + Math.cos(this.time * 2.7) * 0.3;
    const nz = Math.sin(this.time * 1.7) * 0.7 + Math.sin(this.time * 3.1) * 0.3;
    const nPitch = Math.cos(this.time * 1.1) * 0.7 + Math.sin(this.time * 2.1) * 0.3;
    const nYaw = Math.sin(this.time * 1.4) * 0.7 + Math.cos(this.time * 2.5) * 0.3;
    const nRoll = Math.sin(this.time * 1.8) * 0.7 + Math.cos(this.time * 2.9) * 0.3;

    return {
      x: nx * this.maxTranslation * shake,
      y: ny * this.maxTranslation * shake,
      z: nz * this.maxTranslation * shake,
      pitch: nPitch * this.maxRotationRad * shake,
      yaw: nYaw * this.maxRotationRad * shake,
      roll: nRoll * this.maxRotationRad * shake
    };
  }
}
